/**
 * Hook pengambilan data.
 *
 * Pola baku setiap halaman:
 *   const { data, memuat, galat, muat } = useApi<...>('/dashboard/yayasan', { dari, sampai })
 *
 * Semua hook di sini selalu menghasilkan state loading/error/kosong yang bisa
 * dirender — halaman TIDAK boleh tampil putih saat satu endpoint belum tersedia.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  api,
  bangunQuery,
  kodeKesalahan,
  pesanKesalahan,
  type MetaPaginasi,
  type ObyekQuery,
} from './api'
import { useToast } from '@/components/Toast'

export interface HasilApi<T> {
  data: T | null
  meta: MetaPaginasi | null
  memuat: boolean
  galat: string | null
  kodeGalat: string | null
  muat: () => void
}

export interface OpsiApi {
  aktif?: boolean
  /** Bila true, tidak ada permintaan yang dikirim (mis. menunggu pilihan akun). */
  tunda?: boolean
}

/** GET satu endpoint; otomatis batal saat komponen dilepas / parameter berubah. */
export function useApi<T>(path: string | null, query?: ObyekQuery, opsi: OpsiApi = {}): HasilApi<T> {
  const [data, setData] = useState<T | null>(null)
  const [meta, setMeta] = useState<MetaPaginasi | null>(null)
  const [memuat, setMemuat] = useState(false)
  const [galat, setGalat] = useState<string | null>(null)
  const [kodeGalat, setKodeGalat] = useState<string | null>(null)
  const [versi, setVersi] = useState(0)

  const kunci = bangunQuery(query)
  const aktif = opsi.aktif !== false && !opsi.tunda && Boolean(path)

  useEffect(() => {
    if (!aktif || !path) {
      setMemuat(false)
      return
    }
    const kontrol = new AbortController()
    let dibatalkan = false
    setMemuat(true)
    setGalat(null)
    setKodeGalat(null)

    void (async () => {
      try {
        const hasil = await api.getLengkap<unknown>(path, query, kontrol.signal)
        if (dibatalkan) return
        setData((hasil.data ?? null) as T)
        setMeta(hasil.meta ?? null)
      } catch (e) {
        if (dibatalkan) return
        if (e instanceof DOMException && e.name === 'AbortError') return
        setGalat(pesanKesalahan(e))
        setKodeGalat(kodeKesalahan(e))
        setData(null)
      } finally {
        if (!dibatalkan) setMemuat(false)
      }
    })()

    return () => {
      dibatalkan = true
      kontrol.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, kunci, aktif, versi])

  const muat = useCallback(() => setVersi((v) => v + 1), [])

  return { data, meta, memuat, galat, kodeGalat, muat }
}

export interface HasilPaged<T> {
  items: T[]
  meta: MetaPaginasi | null
  memuat: boolean
  galat: string | null
  kodeGalat: string | null
  muat: () => void
  halaman: number
  setHalaman: (n: number) => void
  perHalaman: number
  setPerHalaman: (n: number) => void
  urutan: { sortBy?: string; sortDir: 'asc' | 'desc' }
  setUrutan: (u: { sortBy?: string; sortDir: 'asc' | 'desc' }) => void
}

/** Daftar dengan paginasi SERVER (meta dari backend) + pengurutan server. */
export function usePaged<T>(
  path: string,
  opsi: {
    perHalaman?: number
    query?: ObyekQuery
    aktif?: boolean
    urutanBawaan?: { sortBy?: string; sortDir: 'asc' | 'desc' }
  } = {},
): HasilPaged<T> {
  const perHalamanBawaan = opsi.perHalaman ?? 25
  const [halaman, setHalaman] = useState(1)
  const [perHalaman, setPerHalaman] = useState(perHalamanBawaan)
  const [urutan, setUrutan] = useState<{ sortBy?: string; sortDir: 'asc' | 'desc' }>(
    opsi.urutanBawaan ?? { sortDir: 'desc' },
  )
  const [items, setItems] = useState<T[]>([])
  const [meta, setMeta] = useState<MetaPaginasi | null>(null)
  const [memuat, setMemuat] = useState(false)
  const [galat, setGalat] = useState<string | null>(null)
  const [kodeGalat, setKodeGalat] = useState<string | null>(null)
  const [versi, setVersi] = useState(0)

  const kunciFilter = JSON.stringify(opsi.query ?? {})

  useEffect(() => {
    setHalaman(1)
  }, [kunciFilter])

  const queryGabungan = useMemo<ObyekQuery>(() => {
    const dasar: ObyekQuery = { ...(opsi.query ?? {}) }
    dasar.page = halaman
    dasar.perPage = perHalaman
    jikaAda(dasar, 'sortBy', urutan.sortBy)
    dasar.sortDir = urutan.sortDir
    return dasar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kunciFilter, halaman, perHalaman, urutan.sortBy, urutan.sortDir])

  const aktif = opsi.aktif !== false && Boolean(path)

  useEffect(() => {
    if (!aktif) {
      setMemuat(false)
      return
    }
    const kontrol = new AbortController()
    let dibatalkan = false
    setMemuat(true)
    setGalat(null)
    setKodeGalat(null)

    void (async () => {
      try {
        const hasil = await api.getPaged<T>(path, queryGabungan, kontrol.signal)
        if (dibatalkan) return
        setItems(hasil.items)
        setMeta(hasil.meta)
      } catch (e) {
        if (dibatalkan) return
        if (e instanceof DOMException && e.name === 'AbortError') return
        setGalat(pesanKesalahan(e))
        setKodeGalat(kodeKesalahan(e))
        setItems([])
        setMeta(null)
      } finally {
        if (!dibatalkan) setMemuat(false)
      }
    })()

    return () => {
      dibatalkan = true
      kontrol.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, kunciFilter, halaman, perHalaman, urutan.sortBy, urutan.sortDir, aktif, versi])

  const muat = useCallback(() => setVersi((v) => v + 1), [])

  return {
    items,
    meta,
    memuat,
    galat,
    kodeGalat,
    muat,
    halaman,
    setHalaman,
    perHalaman,
    setPerHalaman,
    urutan,
    setUrutan,
  }
}

function jikaAda(target: ObyekQuery, kunci: string, nilai: unknown): void {
  if (nilai === undefined || nilai === null || nilai === '') return
  target[kunci] = nilai as ObyekQuery[string]
}

export interface HasilAksi {
  jalankan: <T>(fn: () => Promise<T>, opsi?: { sukses?: string; setelah?: () => void }) => Promise<T | null>
  memuat: boolean
  galat: string | null
  bersihkanGalat: () => void
}

/** Pembungkus aksi tulis (POST/PATCH/PUT/DELETE) dengan toast sukses/gagal. */
export function useAksi(): HasilAksi {
  const toast = useToast()
  const [memuat, setMemuat] = useState(false)
  const [galat, setGalat] = useState<string | null>(null)
  const masihHidup = useRef(true)

  useEffect(() => {
    masihHidup.current = true
    return () => {
      masihHidup.current = false
    }
  }, [])

  const jalankan = useCallback(
    async <T,>(fn: () => Promise<T>, opsi?: { sukses?: string; setelah?: () => void }): Promise<T | null> => {
      setMemuat(true)
      setGalat(null)
      try {
        const hasil = await fn()
        if (masihHidup.current && opsi?.sukses) toast.sukses(opsi.sukses)
        opsi?.setelah?.()
        return hasil
      } catch (e) {
        const pesan = pesanKesalahan(e)
        if (masihHidup.current) {
          setGalat(pesan)
          toast.gagal(pesan)
        }
        return null
      } finally {
        if (masihHidup.current) setMemuat(false)
      }
    },
    [toast],
  )

  return { jalankan, memuat, galat, bersihkanGalat: () => setGalat(null) }
}
